import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {catchError, switchMap, tap} from 'rxjs/operators';
import {of} from 'rxjs';
import {LoadError} from '@app/core/store/actions/error.actions';
import {
	CreateShiftDraft,
	CreateShiftDraftSuccess,
	DeleteShiftDraft,
	DeleteShiftDraftSuccess,
	EShiftDraftsActions,
	GetShiftDrafts,
	GetShiftDraftsSuccess,
	ReleaseShiftDraft,
	ReleaseShiftDraftSuccess,
	SetSelectedShiftDraft,
} from '@app/core/store/actions/shift-drafts.actions';
import {ShiftDraftService} from '@app/core/services/api/shiftDraft.service';
import {ShiftDraft} from '@app/core/models/ShiftDraft.model';
import {SnackBar} from '@app/core/services/snackbar.service';
import {GetShifts} from '@app/core/store/actions/schedule.actions';
import {getCurrentState} from '@app/core/helpers';
import {IAppState} from '@app/core/store/state/app.state';
import {Store} from '@ngrx/store';
import {GetStatistics} from '@app/core/store/actions/statistics.actions';

@Injectable()
export class ShiftDraftsEffects {
	@Effect()
	public onGetShiftDrafts$ = this.actions$.pipe(
		ofType<GetShiftDrafts>(EShiftDraftsActions.GetShiftDrafts),
		switchMap((action: GetShiftDrafts) => this.shiftDraftService.getShiftDrafts(action.page, action.page_size, '').pipe(
			switchMap((shiftDrafts: ShiftDraft[]) => [
				new GetShiftDraftsSuccess(shiftDrafts),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);
	@Effect()
	public onCreateShitDraft$ = this.actions$.pipe(
		ofType<CreateShiftDraft>(EShiftDraftsActions.CreateShiftDraft),
		switchMap((action: CreateShiftDraft) => this.shiftDraftService.createShiftDraft(action.shiftDraft).pipe(
			switchMap((shiftDraft: ShiftDraft) => [
				new SetSelectedShiftDraft(shiftDraft),
				new CreateShiftDraftSuccess(shiftDraft),
			]),
			tap(() => this.snackBar.newSuccess('SCHEDULE.X_DRAFT_CREATED', action.shiftDraft.name)),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onDeleteShiftDraft$ = this.actions$.pipe(
		ofType<DeleteShiftDraft>(EShiftDraftsActions.DeleteShiftDraft),
		switchMap((action: DeleteShiftDraft) => this.shiftDraftService.deleteShiftDraft(action.shiftDraft).pipe(
			switchMap((shiftDraft: ShiftDraft) => [
				new DeleteShiftDraftSuccess(action.shiftDraft),
			]),
			tap(() => this.snackBar.newSuccess('SNACKBAR.X_REMOVED', action.shiftDraft.name)),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onReleaseShiftDraft$ = this.actions$.pipe(
		ofType<ReleaseShiftDraft>(EShiftDraftsActions.ReleaseShiftDraft),
		switchMap((action: ReleaseShiftDraft) => this.shiftDraftService.releaseShiftDraft(action.shiftDraft).pipe(
			switchMap((shiftDraft: ShiftDraft) => [
				new SetSelectedShiftDraft(null),
				new ReleaseShiftDraftSuccess(shiftDraft),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);
	@Effect()
	public onSetSelectedShiftDraft$ = this.actions$.pipe(
		ofType<SetSelectedShiftDraft>(EShiftDraftsActions.SetSelectedShiftDraft),
		switchMap(() => {
			if (getCurrentState(this.store).shiftDrafts.isReleasingDraft) {
				return [];
			}
			return [
				new GetShifts(
					getCurrentState(this.store).schedule.selectedDates[0],
					getCurrentState(this.store).schedule.selectedDates[getCurrentState(this.store).schedule.selectedDates.length - 1]
				),
				new GetStatistics(
					getCurrentState(this.store).schedule.selectedDates[0],
					getCurrentState(this.store).schedule.selectedDates[getCurrentState(this.store).schedule.selectedDates.length - 1]
				)
			];
		})
	);

	constructor(
		private actions$: Actions,
		private shiftDraftService: ShiftDraftService,
		public snackBar: SnackBar,
		private store: Store<IAppState>
	) {
	}
}
